import { useEffect, useMemo, useState } from "react";
import client from "../../../api/camasirhaneApi";
import { useAuth } from "../context/CamasirhaneRoleContext";
import { isAdmin } from "../utils/auth";
import { formatNum } from "../utils/constants";
import "../styles/shared.css";

const EMPTY_DEPT = { name: "" };
const EMPTY_MAT = { name: "", category_id: "", default_weight_kg: "" };

export default function DefinitionsScreen() {
  const { user } = useAuth();
  const admin = isAdmin(user);

  const [tab, setTab] = useState("departments");
  const [departments, setDepartments] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Yeni kayıt formları
  const [newDept, setNewDept] = useState(EMPTY_DEPT);
  const [newMat, setNewMat] = useState(EMPTY_MAT);

  // Satır içi düzenleme
  const [editDeptId, setEditDeptId] = useState(null);
  const [editDept, setEditDept] = useState(EMPTY_DEPT);
  const [editMatId, setEditMatId] = useState(null);
  const [editMat, setEditMat] = useState(EMPTY_MAT);

  const [busy, setBusy] = useState(false);

  function flash(msg, isError = false) {
    if (isError) { setError(msg); setMessage(""); }
    else { setMessage(msg); setError(""); }
  }

  function load() {
    setLoading(true);
    Promise.allSettled([
      client.get("/departments/", { params: { include_inactive: 1 } }),
      client.get("/materials/", { params: { include_inactive: 1 } }),
      client.get("/material-categories/"),
    ]).then(([d, m, c]) => {
      if (d.status === "fulfilled") setDepartments(d.value.data);
      if (m.status === "fulfilled") setMaterials(m.value.data);
      if (c.status === "fulfilled") setCategories(c.value.data);
      if (d.status === "rejected" || m.status === "rejected") {
        flash("Tanımlar yüklenemedi.", true);
      }
    }).finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [categories]
  );

  function errMsg(err, fallback) {
    return err?.response?.data?.detail || err?.message || fallback;
  }

  // ---- Departman işlemleri ----
  async function createDept() {
    if (!newDept.name.trim()) return flash("Departman adı zorunludur.", true);
    setBusy(true);
    try {
      await client.post("/departments/", { name: newDept.name.trim(), is_active: true });
      setNewDept(EMPTY_DEPT);
      flash("Departman eklendi.");
      load();
    } catch (err) { flash(errMsg(err, "Departman eklenemedi."), true); }
    finally { setBusy(false); }
  }

  function startEditDept(dept) {
    setEditDeptId(dept.id);
    setEditDept({ name: dept.name });
  }

  async function saveDept(dept) {
    if (!editDept.name.trim()) return flash("Departman adı zorunludur.", true);
    setBusy(true);
    try {
      await client.patch(`/departments/${dept.id}/`, { name: editDept.name.trim() });
      setEditDeptId(null);
      flash("Departman güncellendi.");
      load();
    } catch (err) { flash(errMsg(err, "Departman güncellenemedi."), true); }
    finally { setBusy(false); }
  }

  async function toggleDept(dept) {
    setBusy(true);
    try {
      await client.patch(`/departments/${dept.id}/`, { is_active: !dept.is_active });
      flash(dept.is_active ? "Departman pasifleştirildi." : "Departman aktifleştirildi.");
      load();
    } catch (err) { flash(errMsg(err, "İşlem başarısız."), true); }
    finally { setBusy(false); }
  }

  // ---- Malzeme işlemleri ----
  async function createMat() {
    if (!newMat.name.trim()) return flash("Malzeme adı zorunludur.", true);
    if (!newMat.category_id) return flash("Kategori seçiniz.", true);
    setBusy(true);
    try {
      await client.post("/materials/", {
        name: newMat.name.trim(),
        category_id: Number(newMat.category_id),
        default_weight_kg: newMat.default_weight_kg === "" ? 0 : Number(newMat.default_weight_kg),
        is_active: true,
      });
      setNewMat(EMPTY_MAT);
      flash("Malzeme eklendi.");
      load();
    } catch (err) { flash(errMsg(err, "Malzeme eklenemedi."), true); }
    finally { setBusy(false); }
  }

  function startEditMat(mat) {
    setEditMatId(mat.id);
    setEditMat({
      name: mat.name,
      category_id: String(mat.category_id ?? ""),
      default_weight_kg: mat.default_weight_kg ?? "",
    });
  }

  async function saveMat(mat) {
    if (!editMat.name.trim()) return flash("Malzeme adı zorunludur.", true);
    if (!editMat.category_id) return flash("Kategori seçiniz.", true);
    setBusy(true);
    try {
      await client.patch(`/materials/${mat.id}/`, {
        name: editMat.name.trim(),
        category_id: Number(editMat.category_id),
        default_weight_kg: editMat.default_weight_kg === "" ? 0 : Number(editMat.default_weight_kg),
      });
      setEditMatId(null);
      flash("Malzeme güncellendi.");
      load();
    } catch (err) { flash(errMsg(err, "Malzeme güncellenemedi."), true); }
    finally { setBusy(false); }
  }

  async function toggleMat(mat) {
    setBusy(true);
    try {
      await client.patch(`/materials/${mat.id}/`, { is_active: !mat.is_active });
      flash(mat.is_active ? "Malzeme pasifleştirildi." : "Malzeme aktifleştirildi.");
      load();
    } catch (err) { flash(errMsg(err, "İşlem başarısız."), true); }
    finally { setBusy(false); }
  }

  if (!admin) {
    return (
      <div className="screen">
        <div className="screen-header">
          <h2>Tanımlar</h2>
          <p>Bu ekranı yalnızca yöneticiler görüntüleyebilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Tanımlar</h2>
        <p>Departman ve malzeme cinslerini buradan ekleyin, düzenleyin veya pasifleştirin</p>
      </div>

      <div className="toolbar">
        <div className="tab-group">
          <button className={`ui-btn ${tab === "departments" ? "active" : "ui-btn--ghost"}`} onClick={() => setTab("departments")}>Departmanlar</button>
          <button className={`ui-btn ${tab === "materials" ? "active" : "ui-btn--ghost"}`} onClick={() => setTab("materials")}>Malzeme Cinsleri</button>
        </div>
      </div>

      {message && <p className="status-msg success">{message}</p>}
      {error && <p className="status-msg error">{error}</p>}
      {loading && <p className="status-msg">Yükleniyor...</p>}

      {tab === "departments" && (
        <div className="panel">
          <h2>Departmanlar</h2>

          <div className="def-add-row">
            <input
              className="def-input"
              placeholder="Yeni departman adı (örn. Housekeeping)"
              value={newDept.name}
              onChange={(e) => setNewDept({ name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && createDept()}
            />
            <button className="ui-btn" onClick={createDept} disabled={busy}>Ekle</button>
          </div>

          <div className="grid-wrapper" style={{ marginTop: 14 }}>
            <table className="data-grid def-grid">
              <thead>
                <tr>
                  <th className="label-col">Departman</th>
                  <th style={{ width: 100 }}>Durum</th>
                  <th style={{ width: 220 }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((dept) => (
                  <tr key={dept.id} className={dept.is_active ? "" : "def-inactive"}>
                    <td className="label-col">
                      {editDeptId === dept.id ? (
                        <input
                          className="def-input"
                          value={editDept.name}
                          onChange={(e) => setEditDept({ name: e.target.value })}
                          autoFocus
                        />
                      ) : dept.name}
                    </td>
                    <td>
                      <span className={`def-badge ${dept.is_active ? "on" : "off"}`}>
                        {dept.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td>
                      {editDeptId === dept.id ? (
                        <div className="def-actions">
                          <button className="ui-btn ui-btn--sm" onClick={() => saveDept(dept)} disabled={busy}>Kaydet</button>
                          <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => setEditDeptId(null)} disabled={busy}>Vazgeç</button>
                        </div>
                      ) : (
                        <div className="def-actions">
                          <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => startEditDept(dept)} disabled={busy}>Düzenle</button>
                          <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => toggleDept(dept)} disabled={busy}>
                            {dept.is_active ? "Pasifleştir" : "Aktifleştir"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!departments.length && !loading && (
                  <tr><td colSpan={3}>Henüz departman tanımlı değil.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "materials" && (
        <div className="panel">
          <h2>Malzeme Cinsleri</h2>

          <div className="def-add-row def-add-row--mat">
            <input
              className="def-input"
              placeholder="Yeni malzeme adı (örn. Yatak Takımı)"
              value={newMat.name}
              onChange={(e) => setNewMat((p) => ({ ...p, name: e.target.value }))}
            />
            <select
              className="ui-select"
              value={newMat.category_id}
              onChange={(e) => setNewMat((p) => ({ ...p, category_id: e.target.value }))}
            >
              <option value="">Kategori seçin</option>
              {sortedCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input
              className="def-input def-input--weight"
              type="number"
              min="0"
              step="0.01"
              placeholder="Birim kg"
              value={newMat.default_weight_kg}
              onChange={(e) => setNewMat((p) => ({ ...p, default_weight_kg: e.target.value }))}
            />
            <button className="ui-btn" onClick={createMat} disabled={busy}>Ekle</button>
          </div>

          <div className="grid-wrapper" style={{ marginTop: 14 }}>
            <table className="data-grid def-grid">
              <thead>
                <tr>
                  <th className="label-col">Malzeme</th>
                  <th style={{ width: 170 }}>Kategori</th>
                  <th style={{ width: 100 }}>Birim kg</th>
                  <th style={{ width: 100 }}>Durum</th>
                  <th style={{ width: 220 }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((mat) => (
                  <tr key={mat.id} className={mat.is_active ? "" : "def-inactive"}>
                    <td className="label-col">
                      {editMatId === mat.id ? (
                        <input
                          className="def-input"
                          value={editMat.name}
                          onChange={(e) => setEditMat((p) => ({ ...p, name: e.target.value }))}
                          autoFocus
                        />
                      ) : mat.name}
                    </td>
                    <td>
                      {editMatId === mat.id ? (
                        <select
                          className="ui-select"
                          value={editMat.category_id}
                          onChange={(e) => setEditMat((p) => ({ ...p, category_id: e.target.value }))}
                        >
                          <option value="">Seçin</option>
                          {sortedCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      ) : (mat.category_name || "-")}
                    </td>
                    <td>
                      {editMatId === mat.id ? (
                        <input
                          className="def-input def-input--weight"
                          type="number"
                          min="0"
                          step="0.01"
                          value={editMat.default_weight_kg}
                          onChange={(e) => setEditMat((p) => ({ ...p, default_weight_kg: e.target.value }))}
                        />
                      ) : formatNum(mat.default_weight_kg ?? 0, 3)}
                    </td>
                    <td>
                      <span className={`def-badge ${mat.is_active ? "on" : "off"}`}>
                        {mat.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td>
                      {editMatId === mat.id ? (
                        <div className="def-actions">
                          <button className="ui-btn ui-btn--sm" onClick={() => saveMat(mat)} disabled={busy}>Kaydet</button>
                          <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => setEditMatId(null)} disabled={busy}>Vazgeç</button>
                        </div>
                      ) : (
                        <div className="def-actions">
                          <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => startEditMat(mat)} disabled={busy}>Düzenle</button>
                          <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => toggleMat(mat)} disabled={busy}>
                            {mat.is_active ? "Pasifleştir" : "Aktifleştir"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!materials.length && !loading && (
                  <tr><td colSpan={5}>Henüz malzeme tanımlı değil.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
